// 
// components/FinanceIQ/FinanceIQ.js
//
// Structure mirrors your PoCreation.js exactly:
//   - Same useState / useEffect pattern
//   - Same Tab / TabList / TabPanel from react-tabs
//   - Same AG Grid setup (columnDefs, rowData, AgGridReact)
//   - Same sessionStorage user credentials
//   - Same error state pattern
//   - Same submit-btn / submit-row classNames
//   - Icon from semantic-ui-react
// 

import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Icon } from "semantic-ui-react";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.css";
import LoaderComponent from "../../libraries/loader/loader";
import { useFinanceIQChat } from "./useFinanceIQChat";
import "./FinanceIQ.css";

//  Sub-components 

function SchemaEntityBlock({ entity }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="fiq-entity-block">
            <div
                className={`fiq-entity-header ${open ? "fiq-entity-header--open" : ""}`}
                onClick={() => setOpen(o => !o)}
                style={{ color: entity.iconColor }}
            >
                <span
                    className="fiq-entity-icon"
                    style={{ background: entity.color, color: entity.iconColor }}
                >
                    {entity.icon}
                </span>
                {entity.label}
                <Icon
                    name={open ? "chevron down" : "chevron right"}
                    style={{ marginLeft: "auto", fontSize: "10px", color: "#5a7a9a" }}
                />
            </div>
            {open && (
                <div className="fiq-entity-fields">
                    {entity.fields?.map(f => (
                        <div
                            key={f.name}
                            className="fiq-field-row"
                            title={f.description}
                            style={{ color: f.injected ? "#4aaa7a" : "#4a6a8a" }}
                        >
                            <span>{f.name}</span>
                            <span className="fiq-field-type">{f.type}</span>
                            {f.injected && <span className="fiq-injected-dot">●</span>}
                        </div>
                    ))}
                    <div className="fiq-injected-legend">● = auto-injected into prompt</div>
                </div>
            )}
        </div>
    );
}

function ContextPill({ label, active, color, bg, onToggle }) {
    return (
        <span
            className={`fiq-pill ${active ? "fiq-pill--active" : ""}`}
            style={{
                color,
                background: bg,
                borderColor: active ? color : "transparent",
            }}
            onClick={() => onToggle(label)}
        >
            {label}
        </span>
    );
}

function TraceStep({ step, isLast }) {
    return (
        <div className="fiq-trace-step">
            <div className="fiq-trace-line">
                <div className="fiq-trace-dot" style={{ background: step.dot, boxShadow: `0 0 5px ${step.dot}60` }} />
                {!isLast && <div className="fiq-trace-connector" />}
            </div>
            <div className="fiq-trace-content">
                <div className="fiq-trace-label">{step.label}</div>
                <div className="fiq-trace-text">{step.text}</div>
            </div>
        </div>
    );
}

function AIBubble({ msg }) {
    const CTX_COLORS = {
        "entity:": { bg: "#0a1a28", color: "#4a80b8", border: "#1a3050" },
        "field:":  { bg: "#0a1e14", color: "#4a9a6a", border: "#1a3a20" },
        "calc:":   { bg: "#1e1a08", color: "#aa8830", border: "#3a2a10" },
    };
    const getCtx = (ctx) => {
        const p = Object.keys(CTX_COLORS).find(k => ctx.startsWith(k));
        return CTX_COLORS[p] || CTX_COLORS["entity:"];
    };

    return (
        <div className="fiq-msg fiq-msg--ai">
            <div className="fiq-avatar fiq-avatar--ai">⚖</div>
            <div className="fiq-msg-body">
                {msg.contexts?.length > 0 && (
                    <div className="fiq-badges">
                        {msg.contexts.map(c => {
                            const cs = getCtx(c);
                            return (
                                <span key={c} className="fiq-badge"
                                    style={{ background: cs.bg, color: cs.color, border: `1px solid ${cs.border}` }}>
                                    {c}
                                </span>
                            );
                        })}
                    </div>
                )}
                <div className="fiq-bubble fiq-bubble--ai">{msg.text}</div>
                <div className="fiq-msg-meta">
                    <span className="fiq-timestamp">{msg.timestamp}</span>
                    {msg.tags?.map(t => (
                        <span key={t} className="fiq-tag"
                            style={{ color: t === "error" ? "#d85050" : "#3a6a9a", borderColor: t === "error" ? "#4a1a1a" : "#1a2a3a" }}>
                            {t}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
}

function UserBubble({ msg }) {
    return (
        <div className="fiq-msg fiq-msg--user">
            <div className="fiq-avatar fiq-avatar--user">▸</div>
            <div className="fiq-msg-body">
                <div className="fiq-bubble fiq-bubble--user">{msg.text}</div>
                <div className="fiq-msg-meta fiq-msg-meta--right">
                    <span className="fiq-timestamp">{msg.timestamp}</span>
                </div>
            </div>
        </div>
    );
}

function StreamingBubble({ text }) {
    return (
        <div className="fiq-msg fiq-msg--ai">
            <div className="fiq-avatar fiq-avatar--ai fiq-avatar--streaming">⚖</div>
            <div className="fiq-bubble fiq-bubble--ai fiq-bubble--streaming">
                {text
                    ? <>{text}<span className="fiq-cursor" /></>
                    : <span className="fiq-typing">
                        <span /><span /><span />
                    </span>
                }
            </div>
        </div>
    );
}

//  AG Grid for conversation history 

const HISTORY_COLS = [
    { headerName: "#",        field: "idx",       width: 60,  sortable: true },
    { headerName: "Role",     field: "role",       width: 90,  sortable: true, filter: true },
    { headerName: "Message",  field: "content",    flex: 1,    sortable: true, filter: true },
    { headerName: "Time",     field: "timestamp",  width: 160, sortable: true },
];

const SCOPE_OPTIONS = [
    { label: "Equities",     color: "#4a90d9", bg: "#0d1e30" },
    { label: "Fixed Income", color: "#4aaa7a", bg: "#0a1e14" },
    { label: "Derivatives",  color: "#c8a040", bg: "#1e1808" },
    { label: "FX",           color: "#d86040", bg: "#1e0e08" },
    { label: "Q4-2024",      color: "#4a90d9", bg: "#0d1e30" },
];

const LIVE_METRICS = [
    { label: "NAV",    value: "$2.4B",  change: "+0.8%", pos: true },
    { label: "VaR 95", value: "$12.3M", change: "-2.1%", pos: true },
    { label: "Sharpe", value: "1.42",   change: "+0.03", pos: true },
    { label: "Beta",   value: "0.87",   change: "-0.02", pos: true },
];

//  Main component 

function FinanceIQ() {
    // Matches your PoCreation pattern exactly
    const [userCredentials] = useState(JSON.parse(sessionStorage.getItem("userCredentials")));
    const [activeTab,        setActiveTab]     = useState(0);
    const [globalFilter,     setGlobalFilter]  = useState("");
    const [loading,          setLoading]       = useState(true);
    const [error,            setError]         = useState(null);
    const [inputText,        setInputText]     = useState("");
    const [historyRowData,   setHistoryRowData]= useState([]);

    const gridRef     = useRef(null);
    const messagesEnd = useRef(null);
    const textareaRef = useRef(null);

    const chat = useFinanceIQChat();

    // Stop loader once chat hook initialises
    useEffect(() => {
        if (chat.backendStatus.ok !== null) setLoading(false);
    }, [chat.backendStatus.ok]);

    // Scroll to bottom on new messages
    useEffect(() => {
        messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
    }, [chat.messages, chat.streamingText]);

    // Populate history grid when tab 2 opens
    const handleTabChange = (index) => {
        setActiveTab(index);
        if (index === 1) {
            const rows = chat.messages.map((m, i) => ({
                idx:       i + 1,
                role:      m.role === "ai" ? "Assistant" : "User",
                content:   m.text.slice(0, 120) + (m.text.length > 120 ? "..." : ""),
                timestamp: m.timestamp,
            }));
            setHistoryRowData(rows);
        }
    };

    const handleSend = useCallback(() => {
        const text = inputText.trim();
        if (!text || chat.isStreaming) return;
        setInputText("");
        if (textareaRef.current) textareaRef.current.style.height = "auto";
        chat.sendMessage(text);
    }, [inputText, chat]);

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
    };

    const handleTextareaInput = (e) => {
        setInputText(e.target.value);
        e.target.style.height = "auto";
        e.target.style.height = Math.min(e.target.scrollHeight, 80) + "px";
    };

    const onExportCSV = () => {
        gridRef.current?.api?.exportDataAsCsv();
    };

    const statusColor = chat.backendStatus.ok === true  ? "#27ae60"
                      : chat.backendStatus.ok === false ? "#e74c3c"
                      : "#f39c12";

    if (loading) return <LoaderComponent loaderOpen={true} />;

    return (
        <div className="fiq-root">
            {/* Page heading — same style as your PoCreation */}
            <h2 className="pt-1 pb-1 text-center fiq-page-title">
                FinanceIQ — AI Finance Assistant
            </h2>

            {error && <div style={{ color: "red", padding: "8px 16px" }}>{error}</div>}

            <Tabs selectedIndex={activeTab} onSelect={handleTabChange}>
                <TabList>
                    <Tab>Chat</Tab>
                    <Tab>Conversation History</Tab>
                    <Tab>Schema Explorer</Tab>
                </TabList>

                {/*  TAB 1: Chat  */}
                <TabPanel>
                    <div className="fiq-layout">

                        {/* LEFT — Schema + context */}
                        <div className="fiq-left-panel">
                            <div className="fiq-panel-header">Metadata Schema</div>

                            <div className="fiq-entity-list">
                                {chat.entities.length === 0
                                    ? <div className="fiq-loading-text">Loading schema...</div>
                                    : chat.entities.map(e => <SchemaEntityBlock key={e.id} entity={e} />)
                                }
                            </div>

                            <div className="fiq-scope-section">
                                <div className="fiq-scope-label">Active Context</div>
                                <div className="fiq-pills">
                                    {SCOPE_OPTIONS.map(s => (
                                        <ContextPill
                                            key={s.label}
                                            label={s.label}
                                            color={s.color}
                                            bg={s.bg}
                                            active={chat.activeScopes.includes(s.label)}
                                            onToggle={chat.toggleScope}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* User info — same sessionStorage pattern as PoCreation */}
                            <div className="fiq-user-info">
                                <Icon name="user circle" style={{ color: "#4a90d9", fontSize: "18px" }} />
                                <div>
                                    <div className="fiq-username">{userCredentials?.user_name}</div>
                                    <div className="fiq-userrole">Entity: {userCredentials?.selectedentity}</div>
                                </div>
                            </div>
                        </div>

                        {/* CENTER — Chat messages + input */}
                        <div className="fiq-chat-panel">
                            {/* Header bar */}
                            <div className="fiq-chat-header">
                                <Icon name="balance scale" style={{ fontSize: "20px", color: "#4a90d9" }} />
                                <div>
                                    <div className="fiq-chat-title">FinanceIQ Assistant</div>
                                    <div className="fiq-chat-subtitle">Mistral · Ollama · Metadata-grounded</div>
                                </div>
                                <div className="fiq-status-badge">
                                    <span className="fiq-status-dot" style={{ background: statusColor, boxShadow: `0 0 6px ${statusColor}` }} />
                                    <span style={{ fontSize: "10px", color: statusColor }}>
                                        {chat.backendStatus.message || "Connecting..."}
                                    </span>
                                </div>
                            </div>

                            {/* Messages */}
                            <div className="fiq-messages" role="log" aria-live="polite">
                                {chat.messages.map(msg =>
                                    msg.role === "ai"
                                        ? <AIBubble   key={msg.id} msg={msg} />
                                        : <UserBubble key={msg.id} msg={msg} />
                                )}
                                {chat.isStreaming && <StreamingBubble text={chat.streamingText} />}
                                <div ref={messagesEnd} />
                            </div>

                            {/* Suggestion chips */}
                            {chat.suggestions?.length > 0 && (
                                <div className="fiq-suggestions">
                                    {chat.suggestions.map(chip => (
                                        <button
                                            key={chip}
                                            className="fiq-suggestion-chip"
                                            disabled={chat.isStreaming}
                                            onClick={() => { setInputText(""); chat.sendMessage(chip); }}
                                        >
                                            {chip}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Input — matches your form style */}
                            <div className="fiq-input-bar">
                                <div className={`fiq-input-wrap ${chat.isStreaming ? "fiq-input-wrap--active" : ""}`}>
                                    <textarea
                                        ref={textareaRef}
                                        className="fiq-textarea"
                                        value={inputText}
                                        disabled={chat.isStreaming}
                                        onChange={handleTextareaInput}
                                        onKeyDown={handleKeyDown}
                                        rows={1}
                                        placeholder={
                                            chat.isStreaming
                                                ? "Mistral is thinking..."
                                                : "Ask about P&L, risk, compliance, positions..."
                                        }
                                    />
                                </div>
                                {/* Uses your submit-btn class for consistency */}
                                <button
                                    className="submit-btn fiq-send-btn"
                                    disabled={chat.isStreaming || !inputText.trim()}
                                    onClick={handleSend}
                                >
                                    {chat.isStreaming ? <Icon name="stop" /> : <Icon name="send" />}
                                </button>
                            </div>
                        </div>

                        {/* RIGHT — Inspector */}
                        <div className="fiq-right-panel">
                            <div className="fiq-panel-header">Session</div>
                            <div className="fiq-kv-list">
                                {[
                                    ["entity",         chat.activeEntity],
                                    ["user_name",      userCredentials?.user_name],
                                    ["entity_id",      userCredentials?.selectedentity],
                                    ["tenant_id",      userCredentials?.tenants_id],
                                    ["llm_backend",    "mistral (ollama)"],
                                    ["context_tokens", chat.tokenCount.toLocaleString()],
                                ].map(([k, v]) => (
                                    <div className="fiq-kv-row" key={k}>
                                        <span className="fiq-kv-key">{k}</span>
                                        <span className="fiq-kv-val"
                                            style={{ color: k === "llm_backend" ? "#27ae60" : k === "entity" ? "#4a90d9" : undefined }}>
                                            {v}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div className="fiq-panel-header" style={{ marginTop: "8px" }}>Live Metrics</div>
                            <div className="fiq-metric-grid">
                                {LIVE_METRICS.map(m => (
                                    <div className="fiq-metric-card" key={m.label}>
                                        <div className="fiq-metric-label">{m.label}</div>
                                        <div className="fiq-metric-value" style={{ color: m.pos ? "#27ae60" : "#e74c3c" }}>
                                            {m.value}
                                        </div>
                                        <div className="fiq-metric-change" style={{ color: m.pos ? "#27ae60" : "#e74c3c" }}>
                                            {m.change}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="fiq-panel-header" style={{ marginTop: "8px" }}>Reasoning Trace</div>
                            <div className="fiq-trace-list">
                                {chat.reasoningTrace?.map((step, i) => (
                                    <TraceStep key={i} step={step} isLast={i === chat.reasoningTrace.length - 1} />
                                ))}
                            </div>
                        </div>
                    </div>
                </TabPanel>

                {/*  TAB 2: Conversation History (AG Grid)  */}
                <TabPanel>
                    <div style={{ margin: "10px" }}>
                        <div style={{ display: "flex", alignItems: "center", padding: "10px", gap: "8px" }}>
                            <img
                                src={require("../../assets/images/excel.png")}
                                alt="Export CSV"
                                title="Download CSV"
                                onClick={onExportCSV}
                                style={{ width: "28px", height: "28px", cursor: "pointer" }}
                            />
                            <div style={{ marginLeft: "auto" }}>
                                <input
                                    className="search-input"
                                    type="text"
                                    placeholder="Search..."
                                    value={globalFilter}
                                    onChange={(e) => {
                                        setGlobalFilter(e.target.value);
                                        gridRef.current?.api?.setQuickFilter(e.target.value);
                                    }}
                                    style={{
                                        width: "280px",
                                        backgroundColor: "white",
                                        padding: "7px 12px",
                                        height: "34px",
                                        borderRadius: "6px",
                                        border: "1px solid #ccc",
                                    }}
                                />
                            </div>
                        </div>

                        {/* AG Grid — same pattern as your PoCreation */}
                        <div className="ag-theme-alpine" style={{ height: "480px", width: "100%", borderRadius: "8px" }}>
                            <AgGridReact
                                ref={gridRef}
                                rowData={historyRowData}
                                columnDefs={HISTORY_COLS}
                                pagination={true}
                                paginationPageSize={10}
                                filter={true}
                            />
                        </div>
                    </div>
                </TabPanel>

                {/*  TAB 3: Schema Explorer  */}
                <TabPanel>
                    <div style={{ margin: "10px" }}>
                        <h3 style={{ color: "#4969ab", paddingLeft: "20px", fontSize: "15px" }}>
                            Connected Metadata Entities
                        </h3>
                        <div className="ag-theme-alpine" style={{ height: "500px", width: "100%", borderRadius: "8px", marginTop: "10px" }}>
                            <AgGridReact
                                rowData={
                                    chat.entities.flatMap(e =>
                                        e.fields?.map(f => ({
                                            entity:      e.label,
                                            field:       f.name,
                                            type:        f.type,
                                            injected:    f.injected ? "Yes ●" : "No",
                                            description: f.description,
                                        })) || []
                                    )
                                }
                                columnDefs={[
                                    { headerName: "Entity",      field: "entity",      width: 140, sortable: true, filter: true },
                                    { headerName: "Field",       field: "field",       width: 160, sortable: true, filter: true },
                                    { headerName: "Type",        field: "type",        width: 110, sortable: true, filter: true },
                                    { headerName: "Auto-inject", field: "injected",    width: 110, sortable: true, filter: true },
                                    { headerName: "Description", field: "description", flex: 1,   sortable: true, filter: true },
                                ]}
                                pagination={true}
                                paginationPageSize={15}
                                filter={true}
                            />
                        </div>
                    </div>
                </TabPanel>

            </Tabs>
        </div>
    );
}

export default FinanceIQ;
